<template>
  <div id="admin-logs" class="container-fluid item-list pb-64 admin-logs-wide">
    <v-list-header
      :title="$t('admin.logs.logs')"
      icon="file-text"
      @reload-users="loadLogs"
    >
      <template #input-search>
        <div class="d-flex align-items-center gap-12 flex-wrap">
          <v-select
            v-model="filters.file"
            :options="files"
            :clearable="false"
            :placeholder="$t('admin.logs.file')"
            class="v-select-compact log-file-select"
            @update:modelValue="loadLogs"
          />

          <v-select
            v-model="filters.level"
            :options="levelOptions"
            :reduce="(option: any) => option.key"
            :multiple="true"
            :close-on-select="false"
            :clearable="true"
            :placeholder="$t('admin.logs.level')"
            class="v-select-compact log-level-select"
            @update:modelValue="loadLogs"
          />

          <v-input-text-search
            id="log-search"
            v-model="filters.search"
            :placeholder="$t('admin.logs.search')"
            autocomplete="off"
            class="log-search-input"
            input-class="h-auto py-4"
            input-group-text-class="py-4 px-16"
            type="text"
            @update:modelValue="filters.search = $event; loadLogs()"
          />

          <v-form-input
            v-model="filters.limit"
            type="number"
            :label="$t('admin.logs.limit')"
            form-type="outline"
            class="log-limit-form"
            @blur="loadLogs"
          />

          <v-button
            class="btn-primary btn-sm"
            @click="loadLogs"
          >
            <v-icon icon="refresh-cw" size="sm" class="me-8" />
            {{ $t('admin.logs.refresh') }}
          </v-button>
        </div>
      </template>
    </v-list-header>

    <div class="items-content">
      <div v-if="loading" class="text-center py-96">
        <v-loader />
      </div>

      <div v-else-if="lines.length === 0" class="text-center py-64 text-muted">
        {{ $t('admin.logs.noLogs') }}
      </div>

      <div v-else class="log-table-shell">
        <div
          v-for="(line, index) in lines"
          :key="index"
          class="log-line border-bottom py-12"
          :class="`log-level-${line.level.toLowerCase()}`"
        >
          <div class="d-flex align-items-start gap-12 mb-8">
            <span class="log-datetime text-muted text-nowrap">{{ line.datetime }}</span>
            <span class="log-env badge bg-light text-dark">{{ line.env }}</span>
            <span class="log-level badge" :class="levelBadgeClass(line.level)">{{ line.level }}</span>
          </div>
          <pre class="log-message mb-0">{{ line.message }}</pre>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" name="TheAdminLogs">
import { defineComponent } from 'vue';
import { VFormInput, VInputTextSearch } from '@uniqoders/form';
import vSelect from 'vue-select';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import VButton from '@/components/vendor/basic/button/VButton.vue';
import VLoader from '@/components/vendor/loader/VLoader.vue';
import VListHeader from '@/components/shared/VListHeader.vue';
import api from '@/api';

interface LogLine {
  datetime: string;
  env: string;
  level: string;
  message: string;
}

export default defineComponent({
  name: 'TheAdminLogs',
  components: {
    VListHeader,
    VInputTextSearch,
    VButton,
    VIcon,
    VLoader,
    vSelect,
    VFormInput,
  },
  data() {
    return {
      files: [] as string[],
      lines: [] as LogLine[],
      loading: false,
      filters: {
        file: '' as string,
        level: ['INFO', 'ERROR'] as string[],
        search: '' as string,
        limit: 200 as number,
      },
    };
  },
  computed: {
    levelOptions(): { key: string; label: string }[] {
      const levels = ['DEBUG', 'INFO', 'NOTICE', 'WARNING', 'ERROR', 'CRITICAL', 'ALERT', 'EMERGENCY'];
      return levels.map((level) => ({
        key: level,
        label: level,
      }));
    },
  },
  async created() {
    await this.loadLogs();
  },
  methods: {
    async loadLogs() {
      try {
        this.loading = true;
        const response: any = await api.admin.logs.getLogs({
          file: this.filters.file || undefined,
          level: this.filters.level || undefined,
          search: this.filters.search || undefined,
          limit: this.filters.limit,
        });

        this.files = response?.files ?? [];
        this.lines = response?.lines ?? [];

        if (!this.filters.file && this.files.length > 0) {
          this.filters.file = this.files[0];
        }
      } finally {
        this.loading = false;
      }
    },
    levelBadgeClass(level: string): string {
      switch (level.toUpperCase()) {
        case 'DEBUG':
          return 'bg-light text-dark';
        case 'INFO':
        case 'NOTICE':
          return 'bg-info text-white';
        case 'WARNING':
          return 'bg-warning text-dark';
        case 'ERROR':
        case 'CRITICAL':
        case 'ALERT':
        case 'EMERGENCY':
          return 'bg-danger text-white';
        default:
          return 'bg-secondary text-white';
      }
    },
  },
});
</script>

<style scoped>
.admin-logs-wide .log-file-select {
  min-width: 200px;
}

.admin-logs-wide .log-level-select {
  min-width: 140px;
}

.admin-logs-wide .log-search-input {
  min-width: 220px;
  width: 100%;
}

.admin-logs-wide .log-limit-form {
  min-width: 100px;
}

.log-table-shell {
  font-size: 13px;
}

.log-line {
  padding: 12px 16px;
}

.log-line:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.log-datetime {
  font-family: monospace;
  font-size: 12px;
}

.log-message {
  font-family: monospace;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
  color: #333;
  max-height: 240px;
  overflow: auto;
  background: #f8f9fa;
  padding: 12px;
  border-radius: 4px;
}

.log-level-error .log-message,
.log-level-critical .log-message,
.log-level-alert .log-message,
.log-level-emergency .log-message {
  background: #fff5f5;
}
</style>
